import type { FieldValues, Path } from 'react-hook-form'

import { type CommonFieldProps, usePartitioned } from '@subaio/components/src/HookForm'

import { FieldWrapper, HintContainer, OptionalHint } from '../Shared'
import { ColumnLabel } from './ColumnLabel'
import { Field } from './Field'
import { InputError } from './InputError'
import { InputRow } from './InputRow'
import { Label } from './Label'

type Props<TPath extends string> = CommonFieldProps<TPath> & {
	autoComplete?: string
	type?: 'text' | 'number'
	/** The amount of "characters" that can be submitted and consequently the amount of input rows visible. Defaults to `6` */
	columns?: number
}

/**
 * A input component is split into a set amount of columns to better visualize a "pin" submission.
 * It's important to note that a `type='number'` will always return a string if filled.
 * This is because a number 012345 will be returned as 12345 when parsed otherwise.
 * Default return value is `undefined`
 * @example
 * ```tsx
 * <Partitioned name='name' type='number' label='2FA code' />
 * ```
 */
export const Partitioned = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	showOptionalHint: fieldShowOptionalHint,
	isFullWidth = false,
	isRequired = false,
	type = 'text',
	isDisabled = false,
	style,
	className,
	name,
	isReadOnly = false,
	label,
	hint,
	placeholder,
	shouldAutofocus = false,
	validate = {},
	columns = 6,
	autoComplete = 'off',
	testId,
}: Props<TPath>) => {
	const { id, labelId, error, hasError, inputRef, showOptionalHint, inputProps, getInputProps } = usePartitioned<
		TFieldValues,
		TPath
	>({
		isRequired,
		type,
		isDisabled,
		name,
		isReadOnly,
		showOptionalHint: fieldShowOptionalHint,
		placeholder,
		shouldAutofocus,
		validate,
		columns,
		autoComplete,
	})

	return (
		<FieldWrapper isFullWidth={isFullWidth}>
			<ColumnLabel>
				<Label id={labelId}>
					<span>
						{label}{' '}
						{(fieldShowOptionalHint ?? showOptionalHint) && !isRequired && <OptionalHint>(Optional)</OptionalHint>}
					</span>
				</Label>
				<input {...inputProps} data-test-id={testId} />
				<InputRow id={`${id}-group`}>
					{Array.from({ length: columns }).map((_, index) => (
						<Field
							key={index}
							inputProps={getInputProps(index)}
							hasError={hasError}
							isDisabled={isDisabled}
							isReadOnly={isReadOnly}
							shouldAutofocus={shouldAutofocus}
							style={style}
							className={className}
							onClick={(event) => {
								inputRef.current?.focus()
								if (event.defaultPrevented) return undefined
							}}
						/>
					))}
				</InputRow>
				<HintContainer isVisible={Boolean(hint) && !hasError}>{hint}</HintContainer>
				<InputError hasError={hasError} errorMessage={error?.message} />
			</ColumnLabel>
		</FieldWrapper>
	)
}
